import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {ActivityIndicator, Animated, Easing, Modal, View} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {create} from '@bufbuild/protobuf';
import {SafeAreaInsetsContext} from 'react-native-safe-area-context';
import {Message, MessageType, Severity} from '../../generated/notification_pb';
import {AckMessageRequestSchema} from '../../generated/bay_session_shared_pb';
import {colors} from '../assets/styles/colors';
import {useConnectClients} from '../grpc/ConnectClientContext';
import Btn from './Btn';
import Div from '../lib/Div';
import P from '../lib/P';
import {toastDismissDelayMs} from '../utils/toastDismissDelay';
import {StoreContext} from './StoreContextProvider';

type Props = {
  messages: readonly Message[], identityKey: string, showConfirmation: boolean, onDismiss: (messageId: string) => void
};

type MessageCardProps = {
  message: Message, pending: boolean, ackFailed: boolean, onAck: (messageId: string) => void,
  onDismiss: (messageId: string) => void, showDismissButton?: boolean
};

type ConfirmationMessageModalProps = {
  message?: Message, visible: boolean, pending: boolean, ackFailed: boolean, onAck: (messageId: string) => void,
  onDismiss: (messageId: string) => void
};

type ToastAnimationKind = 'generic' | 'wallet' | 'free-play';

type CelebrationParticle = {
  color: string, left: `${number}%`, translateX: number, translateY: number, rotation: string
};

const ACK_TIMEOUT_MS = 10000;
const TOAST_ANIMATION_DURATION_MS: Record<ToastAnimationKind, number> = {generic: 220, wallet: 360, 'free-play': 720};
const FULL_SWING_MESSAGE_COLORS = {
  surface: '#131314', secondarySurface: '#252526', border: '#4C4C4C', primary: '#3369FF', body: '#BBBCBF',
  info: '#46BDFB', success: '#1E9428', warning: '#FABF41', error: '#F55656', iconBackground: 'rgba(255, 255, 255, 0.1)'
};
const FREE_PLAY_CELEBRATION_PARTICLES: readonly CelebrationParticle[] = [
  {color: '#3369FF', left: '12%', translateX: -14, translateY: 42, rotation: '-120deg'},
  {color: '#46BDFB', left: '27%', translateX: 10, translateY: 50, rotation: '135deg'},
  {color: '#FABF41', left: '43%', translateX: -8, translateY: 44, rotation: '-160deg'},
  {color: '#1E9428', left: '58%', translateX: 12, translateY: 52, rotation: '150deg'},
  {color: '#F55656', left: '73%', translateX: -10, translateY: 46, rotation: '-135deg'},
  {color: '#FFFFFF', left: '88%', translateX: 14, translateY: 40, rotation: '125deg'}
];

const addToSet = (current: ReadonlySet<string>, value: string) => {
  if (current.has(value)) return current;
  const next = new Set(current);
  next.add(value);
  return next;
};

const removeFromSet = (current: ReadonlySet<string>, value: string) => {
  if (!current.has(value)) return current;
  const next = new Set(current);
  next.delete(value);
  return next;
};

const retainActiveIds = (current: ReadonlySet<string>, activeIds: ReadonlySet<string>) => {
  const next = new Set(Array.from(current).filter(id => activeIds.has(id)));
  return next.size === current.size ? current : next;
};

const timestampMillis = (timestamp: Message['expiresAt']): number | null => {
  if (!timestamp) return null;
  return Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1e6);
};

const richPayloadKind = (message: Message): string | null => {
  const kind = message.body?.richPayload?.kind;
  return typeof kind === 'string' ? kind : null;
};

// SMG-4371: only rich_payload.kind is read; amount, currency and balance are not rendered yet.
const toastAnimationKind = (message: Message): ToastAnimationKind => {
  switch (richPayloadKind(message)) {
    case 'wallet_balance_changed':
      return 'wallet';
    case 'free_play_turned_over':
      return 'free-play';
    default:
      return 'generic';
  }
};

const severityColor = (severity: Severity): string => {
  switch (severity) {
    case Severity.WARN:
      return FULL_SWING_MESSAGE_COLORS.warning;
    case Severity.CELEBRATE:
      return FULL_SWING_MESSAGE_COLORS.success;
    case Severity.ERROR:
      return FULL_SWING_MESSAGE_COLORS.error;
    case Severity.INFO:
    case Severity.UNSPECIFIED:
    default:
      return FULL_SWING_MESSAGE_COLORS.info;
  }
};

const messageIcon = (message: Message): React.ComponentProps<typeof MaterialCommunityIcons>['name'] => {
  const kind = richPayloadKind(message);
  if (kind === 'wallet_balance_changed') return 'cash-plus';
  if (kind === 'free_play_turned_over') return 'star-circle';

  switch (message.body?.icon) {
    case 'success':
    case 'top-off':
      return 'check-circle';
    case 'warn':
    case 'idle-warning':
      return 'alert-circle-outline';
    case 'error':
      return 'alert-circle';
    case 'celebrate':
    case 'free-play-turnover':
      return 'party-popper';
    default:
      return 'bell-outline';
  }
};

export const hasConfirmationMessage = (messages: readonly Message[]): boolean => messages.some(message => message.messageType === MessageType.CONFIRMATION);

const MessageText = ({message}: { message: Message }) => {
  const body = message.body;
  const accent = severityColor(message.severity);

  return (
    <Div row alignItems="flex-start" pr={30}>
      <Div w={38} h={38} mr={12} alignItems="center" justifyContent="center" borderWidth={1} borderColor={accent}
           rounded={19} bg={FULL_SWING_MESSAGE_COLORS.iconBackground}>
        <MaterialCommunityIcons
          name={messageIcon(message)} size={22} color={accent} testID={`message-icon-${message.messageId}`}
        />
      </Div>

      <Div flex={1}>
        <P color={colors.whiteText} fontFamily="DavisSans-Medium" fs={16} testID={`message-title-${message.messageId}`}>
          {body?.title || 'Notification'}
        </P>

        {!!body?.body && (
          <P mt={4} color={FULL_SWING_MESSAGE_COLORS.body} fontFamily="DavisSans-Regular" fs={14}
             testID={`message-body-${message.messageId}`}>
            {body.body}
          </P>
        )}
      </Div>
    </Div>
  );
};

const MessageCard = ({message, pending, ackFailed, onAck, onDismiss, showDismissButton}: MessageCardProps) => {
  const accent = severityColor(message.severity);
  const cta = message.body?.cta;
  const ctaPending = !!cta?.acks && pending;

  return (
    <Div testID={`message-card-${message.messageId}`} position="relative" mt={8} p={14} borderLeftWidth={4} rounded={12}
         bg={FULL_SWING_MESSAGE_COLORS.surface} borderWidth={1} borderColor={FULL_SWING_MESSAGE_COLORS.border}
         borderLeftColor={accent}>
      <MessageText message={message}/>

      {showDismissButton && (
        <Btn accessibilityLabel="Dismiss notification" accessibilityRole="button" position="absolute" top={8} right={8}
             w={32} h={32} alignItems="center" justifyContent="center" onPress={() => onDismiss(message.messageId)}
             testID={`message-dismiss-${message.messageId}`}>
          <MaterialCommunityIcons name="close" size={20} color={FULL_SWING_MESSAGE_COLORS.body}/>
        </Btn>
      )}

      {!!cta && (
        <Btn accessibilityRole="button" disabled={ctaPending} minH={40} mt={12} alignItems="center"
             justifyContent="center" opacity={ctaPending ? 0.65 : 1}
             bg={cta.style === 'secondary' ? FULL_SWING_MESSAGE_COLORS.secondarySurface : FULL_SWING_MESSAGE_COLORS.primary}
             borderWidth={cta.style === 'secondary' ? 1 : undefined} testID={`message-cta-${message.messageId}`}
             borderColor={cta.style === 'secondary' ? FULL_SWING_MESSAGE_COLORS.border : undefined} rounded={4}
             onPress={() => cta.acks ? onAck(message.messageId) : onDismiss(message.messageId)}>
          {ctaPending ? (
            <ActivityIndicator color={colors.white}/>
          ) : (
            <P color={colors.white} fontFamily="DavisSans-Medium" fs={14}>{cta.label}</P>
          )}
        </Btn>
      )}

      {ackFailed && (
        <P accessibilityRole="alert" mt={8} color={FULL_SWING_MESSAGE_COLORS.error} fontFamily="DavisSans-Regular"
           fs={13} testID={`message-ack-error-${message.messageId}`}>
          We couldn't confirm that. Please try again.
        </P>
      )}
    </Div>
  );
};

const toastMotionStyle = (kind: ToastAnimationKind, progress: Animated.Value) => {
  switch (kind) {
    case 'wallet':
      return {
        opacity: progress.interpolate({inputRange: [0, 0.35, 1], outputRange: [0, 1, 1]}),
        transform: [{translateX: progress.interpolate({inputRange: [0, 0.82, 1], outputRange: [36, -3, 0]})}]
      };
    case 'free-play':
      return {
        opacity: progress.interpolate({inputRange: [0, 0.25, 1], outputRange: [0, 1, 1]}),
        transform: [{scale: progress.interpolate({inputRange: [0, 0.58, 1], outputRange: [0.92, 1.04, 1]})}]
      };
    case 'generic':
    default:
      return {
        opacity: progress, transform: [{translateY: progress.interpolate({inputRange: [0, 1], outputRange: [14, 0]})}]
      };
  }
};

const FreePlayCelebration = ({messageId, progress}: { messageId: string, progress: Animated.Value }) => (
  <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"
        pointerEvents="none" testID={`message-celebration-${messageId}`}
        style={{position: 'absolute', top: -10, right: 0, left: 0, height: 72, zIndex: 1}}>
    {FREE_PLAY_CELEBRATION_PARTICLES.map((particle, index) => (
      <Animated.View
        key={`${particle.left}-${particle.color}`}
        style={{
          position: 'absolute', top: 0, left: particle.left, width: 6, height: 10, borderRadius: 2,
          backgroundColor: particle.color,
          opacity: progress.interpolate({inputRange: [0, 0.12, 0.72, 1], outputRange: [0, 1, 0.8, 0]}),
          transform: [
            {translateX: progress.interpolate({inputRange: [0, 1], outputRange: [0, particle.translateX]})},
            {translateY: progress.interpolate({inputRange: [0, 1], outputRange: [-4 - index, particle.translateY]})},
            {rotate: progress.interpolate({inputRange: [0, 1], outputRange: ['0deg', particle.rotation]})},
            {scale: progress.interpolate({inputRange: [0, 0.2, 1], outputRange: [0.6, 1, 0.8]})}
          ]
        }}
      />
    ))}
  </View>
);

const ToastMessage = (props: MessageCardProps) => {
  const {message, pending, ackFailed, onDismiss} = props;
  const progress = useRef(new Animated.Value(0)).current;
  const expiresAtMs = timestampMillis(message.expiresAt);
  const animationKind = toastAnimationKind(message);

  useEffect(() => {
    progress.setValue(0);
    const animation = Animated.timing(progress, {
      toValue: 1, duration: TOAST_ANIMATION_DURATION_MS[animationKind], easing: Easing.out(Easing.cubic),
      useNativeDriver: true
    });
    animation.start();

    return () => animation.stop();
  }, [animationKind, progress]);

  useEffect(() => {
    if (pending || ackFailed) return;

    const delay = toastDismissDelayMs(expiresAtMs, Date.now());
    const timer = setTimeout(() => onDismiss(message.messageId), delay);
    return () => clearTimeout(timer);
  }, [ackFailed, expiresAtMs, message.messageId, onDismiss, pending]);

  return (
    <View accessibilityLiveRegion="polite" testID={`message-toast-${message.messageId}`}>
      <Animated.View testID={`message-animation-${animationKind}-${message.messageId}`}
                     style={toastMotionStyle(animationKind, progress)}>
        <MessageCard
          message={message} pending={pending} ackFailed={ackFailed} onAck={props.onAck} onDismiss={onDismiss}
          showDismissButton
        />
      </Animated.View>

      {animationKind === 'free-play' && (
        <FreePlayCelebration messageId={message.messageId} progress={progress}/>
      )}
    </View>
  );
};

const ConfirmationMessageModal = (props: ConfirmationMessageModalProps) => {
  const {message, visible, pending, ackFailed} = props;
  if (!message) return null;

  const accent = severityColor(message.severity);
  const buttonLabel = message.body?.cta?.label || 'Got it';
  const ctaAcks = message.body?.cta?.acks !== false;

  // Confirmation messages require an explicit CTA; hardware back must not dismiss the modal.
  return (
    <Modal animationType="fade" hardwareAccelerated={false} onRequestClose={() => undefined}
           statusBarTranslucent={false} transparent visible={visible}>
      <Div flex={1} alignItems="center" justifyContent="center" px={16} bg={colors.filter_modal_bg}>
        <Div accessibilityViewIsModal w="100%" maxW={343} overflow="hidden" bg="#131314" rounded={16}
             testID={`message-confirmation-${message.messageId}`}>
          <Div minH={48} alignItems="center" justifyContent="center" px={16} bg="#252526">
            <P color={colors.whiteText} fontFamily="DavisSans-Medium" fs={16} ta="center">
              {message.body?.title || 'Notification'}
            </P>
          </Div>

          <Div alignItems="center" p={16} pt={32}>
            <Div w={64} h={64} mb={20} alignItems="center" justifyContent="center" borderWidth={2} borderColor={accent}
                 rounded={32} bg={FULL_SWING_MESSAGE_COLORS.iconBackground}>
              <MaterialCommunityIcons name={messageIcon(message)} size={42} color={accent}/>
            </Div>

            {!!message.body?.body && (
              <P mb={20} color={colors.whiteText} fontFamily="DavisSans-Regular" fs={15} lh={21} ta="center">
                {message.body.body}
              </P>
            )}

            <Btn accessibilityRole="button" disabled={pending} w="100%" minH={56} alignItems="center" rounded={6}
                 onPress={() => ctaAcks ? props.onAck(message.messageId) : props.onDismiss(message.messageId)}
                 justifyContent="center" bg="#3369FF" testID={`message-confirmation-cta-${message.messageId}`}
                 opacity={pending ? 0.65 : 1}>
              {pending ? (
                <ActivityIndicator color={colors.white}/>
              ) : (
                <P color={colors.white} fontFamily="DavisSans-Medium" fs={16}>{buttonLabel}</P>
              )}
            </Btn>

            {ackFailed && (
              <>
                <P accessibilityRole="alert" mt={12} color={FULL_SWING_MESSAGE_COLORS.error} fs={13} ta="center"
                   fontFamily="DavisSans-Regular" testID={`message-confirmation-error-${message.messageId}`}>
                  We couldn't confirm that. Please try again.
                </P>
                <Btn accessibilityRole="button" accessibilityLabel="Dismiss confirmation" w="100%" minH={48} mt={12}
                     alignItems="center" justifyContent="center" bg={FULL_SWING_MESSAGE_COLORS.secondarySurface}
                     borderWidth={1} borderColor={FULL_SWING_MESSAGE_COLORS.border} rounded={6}
                     onPress={() => props.onDismiss(message.messageId)}
                     testID={`message-confirmation-dismiss-${message.messageId}`}>
                  <P color={colors.white} fontFamily="DavisSans-Medium" fs={14}>Dismiss</P>
                </Btn>
              </>
            )}
          </Div>
        </Div>
      </Div>
    </Modal>
  );
};

const MessageOutlet = ({messages, identityKey, showConfirmation, onDismiss}: Props) => {
  const {baySession} = useConnectClients();
  const {bayPlayerJwt} = useContext(StoreContext);
  const insets = useContext(SafeAreaInsetsContext);
  const [pendingAckIds, setPendingAckIds] = useState<ReadonlySet<string>>(() => new Set());
  const [failedAckIds, setFailedAckIds] = useState<ReadonlySet<string>>(() => new Set());
  const inFlightAckIdsRef = useRef<Map<string, symbol>>(new Map());
  const identityKeyRef = useRef(identityKey);
  const mountedRef = useRef(false);

  const activeMessageIds = useMemo(() => {
    return new Set(messages.map(message => message.messageId));
  }, [messages]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      inFlightAckIdsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (identityKeyRef.current === identityKey) return;
    identityKeyRef.current = identityKey;
    inFlightAckIdsRef.current.clear();
    setPendingAckIds(new Set());
    setFailedAckIds(new Set());
  }, [identityKey]);

  useEffect(() => {
    setPendingAckIds(current => retainActiveIds(current, activeMessageIds));
    setFailedAckIds(current => retainActiveIds(current, activeMessageIds));
  }, [activeMessageIds]);

  const ackMessage = useCallback(async (messageId: string) => {
    if (inFlightAckIdsRef.current.has(messageId)) return;

    if (!bayPlayerJwt) {
      setFailedAckIds(current => addToSet(current, messageId));
      return;
    }

    const requestIdentityKey = identityKeyRef.current;
    const requestMarker = Symbol(messageId);
    inFlightAckIdsRef.current.set(messageId, requestMarker);
    setPendingAckIds(current => addToSet(current, messageId));
    setFailedAckIds(current => removeFromSet(current, messageId));

    try {
      await baySession.ackMessage(
        create(AckMessageRequestSchema, {messageId}),
        {headers: {Authorization: `Bearer ${bayPlayerJwt}`}, timeoutMs: ACK_TIMEOUT_MS}
      );

      if (mountedRef.current && identityKeyRef.current === requestIdentityKey) {
        onDismiss(messageId);
      }

    } catch (error) {
      if (mountedRef.current && identityKeyRef.current === requestIdentityKey) {
        setPendingAckIds(current => removeFromSet(current, messageId));
        setFailedAckIds(current => addToSet(current, messageId));
      }
      console.warn('[MessageOutlet] message ack failed', error);

    } finally {
      if (inFlightAckIdsRef.current.get(messageId) === requestMarker) {
        inFlightAckIdsRef.current.delete(messageId);
      }
    }
  }, [bayPlayerJwt, baySession, onDismiss]);

  const confirmation = messages.find(message => message.messageType === MessageType.CONFIRMATION);
  const toasts = messages.filter(message => message.messageType === MessageType.TOAST);
  const playerPersistent = messages.filter(message => message.messageType === MessageType.PERSISTENT_PLAYER);

  return (
    <Div pointerEvents="box-none" position="absolute" top={0} right={0} bottom={0} left={0}>
      {(toasts.length > 0 || playerPersistent.length > 0) && (
        <Div pointerEvents="box-none" position="absolute" right={0} bottom={0} left={0} px={12}
             pb={(insets?.bottom ?? 0) + 12} testID="message-outlet">
          {playerPersistent.map(message => (
            <MessageCard
              key={message.messageId} message={message} pending={pendingAckIds.has(message.messageId)}
              ackFailed={failedAckIds.has(message.messageId)} onAck={ackMessage} onDismiss={onDismiss}
            />
          ))}

          {toasts.map(message => (
            <ToastMessage
              key={message.messageId} message={message} pending={pendingAckIds.has(message.messageId)}
              ackFailed={failedAckIds.has(message.messageId)} onAck={ackMessage} onDismiss={onDismiss}
            />
          ))}
        </Div>
      )}

      <ConfirmationMessageModal
        message={confirmation} visible={showConfirmation && !!confirmation} onAck={ackMessage}
        pending={!!confirmation && pendingAckIds.has(confirmation.messageId)}
        ackFailed={!!confirmation && failedAckIds.has(confirmation.messageId)}
        onDismiss={onDismiss}
      />
    </Div>
  );
};

export default MessageOutlet;
